import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { Subject, finalize, takeUntil } from 'rxjs';

import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { OrderApiService } from 'v2/modules/shared/services/api/order/order-api.service';

import { InvoiceStatus, ISalesOrderWithInvoicing } from './batch-invoice.interface';

@Component({
  selector: 'lum-batch-create-invoice',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
  ],
  templateUrl: './batch-create-invoice.component.html',
  styleUrls: ['./batch-create-invoice.component.scss'],
})
export class BatchCreateInvoiceComponent implements OnInit, OnDestroy {
  salesOrders :ISalesOrderWithInvoicing[] = [];
  forInvoicingOrders :ISalesOrderWithInvoicing[] = [];
  cantBeInvoicedOrders :ISalesOrderWithInvoicing[] = [];
  loading :boolean = false;
  hasUpdates :boolean = false;
  invoiceStatus = InvoiceStatus;
  #onDestroy$ = new Subject<void>();

  constructor(
    private dialogRef :MatDialogRef<BatchCreateInvoiceComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data :{ salesOrders :ISalesOrderWithInvoicing[] },
    private orderApiService :OrderApiService,
    private snackBar :MatSnackBar,
    private cd :ChangeDetectorRef,
  ) {
    this.salesOrders = this.data.salesOrders;
  }

  ngOnInit() :void {
    // Process and categorize the orders
    this.salesOrders.forEach((order :ISalesOrderWithInvoicing) => {
      // Add initial invoicing status for tracking
      order.invoicingStatus = InvoiceStatus.PENDING;

      // Check if the order can be invoiced
      // For now, we're simplifying and just checking if it exists
      if (order && order.id) {
        this.forInvoicingOrders.push(order);
      } else {
        if (!order.invoicingErrors) {
          order.invoicingErrors = [];
        }
        order.invoicingErrors.push('Invalid order data');
        this.cantBeInvoicedOrders.push(order);
      }
    });
  }

  async createInvoices() : Promise<void> {
    this.loading = true;

    // Filter for orders in PENDING state
    const pendingOrders = this.forInvoicingOrders.filter(
      (order: ISalesOrderWithInvoicing) => order.invoicingStatus === InvoiceStatus.PENDING
    );

    for (const order of pendingOrders) {
      order.invoicingStatus = InvoiceStatus.PROCESSING;
      await new Promise(resolve => setTimeout(resolve, 1000)); // per-call delay to avoid overwhelming the API (e.g., QuickBooks ThrottleExceeded)

      // Call the API to create invoice
      this.orderApiService
        .createSalesOrderInvoice(order.id)
        .pipe(
          takeUntil(this.#onDestroy$),
          finalize(() => {
            // Check if all orders are processed
            this.isFinished();
          })
        )
        .subscribe({
          next: () => {
            order.invoicingStatus = InvoiceStatus.COMPLETED;
            this.hasUpdates = true;
          },
          error: (errorResp) => {
            order.invoicingStatus = InvoiceStatus.FAILED;
            if (errorResp && errorResp.error) {
              order.invoicingError = errorResp.error || errorResp.error.error || 'Failed to create invoice';
            } else {
              order.invoicingError = 'Failed to create invoice';
            }
          },
        });
    }
  }

  close() :void {
    this.dialogRef.close(this.hasUpdates);
  }

  private isFinished() :void {
    // Check if any orders are still processing
    this.loading =
      this.forInvoicingOrders.filter(
        (order :ISalesOrderWithInvoicing) =>
          order.invoicingStatus === InvoiceStatus.PROCESSING,
      ).length > 0;

    if (!this.loading) {
      this.snackBar.open('Invoice creation process completed.', 'Close', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  ngOnDestroy() :void {
    this.#onDestroy$.next();
    this.#onDestroy$.complete();
  }
}
